import React, { useState } from 'react';
import Hero from '../../../components/Hero/Hero';
import Pathfinder from '../../../components/Pathfinder/Pathfinder';
import Metadata from '../../../components/Metadata/Metadata';
import Section from '../../../components/Section/Section';
import Card from '../../../components/Card/Card';
import GlobalCallout from '../../../components/Global/CalloutDefault';
import { Box, Typography } from '@mui/material';
import Wallpaper from '../../../components/Wallpaper/Wallpaper';
import Accordion from '../../../components/Accordion/Accordion';
import Image from '../../../components/Image/Image';
import ContentSplit from '../../../components/ContentSplit/ContentSplit';
import ListItem from '../../../components/ListItem/ListItem';
import Button from '../../../components/Button/Button';
import { Link } from 'react-router-dom';

const PageContent: React.FC = () => {
  const [expandAll, setExpandAll] = useState(false);

  const toggleAll = () => {
    setExpandAll(!expandAll);
  };

  return (
    <Box>
      <Metadata
        title="Public Sector Solutions | General Genomics"
        description="Learn how General Genomics empowers government agencies with secure, compliant data solutions. Discover how we enhance efficiency, support policy, and improve public services."
      />

      <Hero
        helper="Industries &gt; Government"
        headline="Optimizing public sector outcomes through advanced analytics and AI"
      />

      <Section theme="" className="cards-large-icons">
        <h2 className="action-on-view action-fade-in">
          Transform Public Services with Data
        </h2>
        <p className="action-on-view action-fade-in">
          At General Genomics, we specialize in data-driven solutions that
          empower government agencies to optimize operations, enhance
          decision-making, and deliver better services to the public. Our
          tailored offerings in AI and machine learning enable policy
          development, improve workforce efficiency, and support public health
          initiatives, all while ensuring compliance with the highest security
          standards. With our expertise, you can harness the power of data to
          drive meaningful change. Contact us today and see how our solutions
          can benefit your agency.
        </p>
        <Image
          path="/images/library/military-meeting-team.webp"
          size="wide"
          className="action-on-view action-fade-in"
          alt="This is a wide image."
        />
      </Section>

      <ContentSplit
        image="/images/library/government-officials.webp"
        position="right"
      >
        <p className="helper action-on-view action-fade-in">
          Unlock Government Solutions
        </p>
        <h2 className="action-on-view action-fade-in">
          How Data Solutions Empower Government Agencies
        </h2>

        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>AI-Powered Decision Making:</strong> Leverage machine
            learning to inform policies, predict trends, and optimize government
            resource allocation, ensuring smarter decisions.
          </p>
        </ListItem>

        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Improved Service Delivery:</strong> Our predictive analytics
            tools help streamline public services by anticipating needs and
            improving workforce management.
          </p>
        </ListItem>

        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Public Health Solutions:</strong> Integrating bioinformatics
            and data analytics enhances public health initiatives, providing
            insights that improve health outcomes.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Secure Data Governance:</strong> We prioritize compliance
            and data security, ensuring that all solutions adhere to government
            regulations and protect sensitive information.
          </p>
        </ListItem>
      </ContentSplit>

      <Section theme="dark" className="section-cross-linking">
        <h2 className="action-on-view action-fade-in">
          Explore Our Genomic Solutions
        </h2>
        <p className="action-on-view action-fade-in">
          Dive deeper into our offerings and discover how General Genomics is
          revolutionizing data solutions for public health and governance.
        </p>
        <div className="card-group">
          <Card
            icon="precision_manufacturing"
            headline="Our Capabilities"
            subhead="Explore our cutting-edge technical expertise in genomics data solutions, tailored to your unique needs."
            btnURL="/our-approach/our-capabilities"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="security"
            headline="Data Governance"
            subhead="Learn how we ensure secure handling and compliance of sensitive genomic data."
            btnURL="/our-approach/data-governance-and-compliance"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="public"
            headline="Public Health"
            subhead="Discover our applications for advancing public health with robust genomic data solutions."
            btnURL="/industries/government/public-health-and-policy"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="data_object"
            headline="Data Portal"
            subhead="Access our comprehensive datasets and tools designed for public sector innovation."
            btnURL="/data-portal"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
        </div>
      </Section>

      <Wallpaper asset="../../images/library/government-officials-3.webp"></Wallpaper>

      <Section>
        <Typography variant="h2" className="action-on-view action-fade-in">
          Frequently asked questions about Public Sector Data Solutions
        </Typography>
        <Typography className="action-on-view action-fade-in">
          Learn how General Genomics helps government agencies optimize services
          with AI-driven data solutions, ensuring security and compliance.
        </Typography>
        <Box className="accordion-wrapper action-on-view action-fade-in">
          <Button
            className="accordion-button"
            type="outlined"
            onClick={toggleAll}
            icon={expandAll ? 'expand_less' : 'expand_more'}
            label={expandAll ? 'Collapse All' : 'Expand All'}
          />
          <Accordion
            isOpen={expandAll}
            headline="What types of data solutions does General Genomics provide for government agencies?"
          >
            General Genomics offers tailored data services focused on improving
            operational efficiency, supporting policy development, and enhancing
            public services through secure and compliant data solutions.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How can AI and machine learning improve public sector decision-making and service delivery?"
          >
            By leveraging AI and machine learning, General Genomics helps{' '}
            <Link to="/industries/government">government</Link> agencies
            optimize decision-making processes, enhance predictive analytics,
            and improve service delivery across various public sector
            applications.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="What are some examples of how General Genomics' solutions are used in the public sector?"
          >
            Our solutions are applied in areas like predictive analytics,
            workforce optimization, and{' '}
            <Link to="/industries/government/bioinformatics">
              bioinformatics,
            </Link>{' '}
            improving operational efficiency and public service outcomes in
            sectors such as healthcare, public health, and workforce management.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How does General Genomics ensure the security and compliance of its data solutions?"
          >
            We prioritize data governance and compliance by adhering to strict
            protocols for secure data handling, ensuring that all solutions meet{' '}
            <Link to="/industries/government">government</Link> standards for
            privacy and security.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="Where can I find more information about General Genomics’ public sector data tools and solutions?"
          >
            For more details, explore our{' '}
            <Link to="/data-portal">Data Portal,</Link> or contact us to discuss
            how our customized solutions can benefit your{' '}
            <Link to="/industries/government">government</Link> agency.
          </Accordion>
        </Box>
      </Section>

      <Section theme="dark" bgName="lines" className="section-call-to-action">
        <Typography variant="h2" className="action-on-view action-fade-in">
          Why partnering with General Genomics transforms bioinformatics
          research and innovation
        </Typography>
        <Box className="card-group">
          <Card
            icon="check"
            headline="Advanced Solutions in Analytics"
            subhead="General Genomics combines analytics and innovation with cutting-edge algorithms, large language models, machine learning, and predictive analytics to transform synthetic systems and complex data insights."
            className="action-on-view action-fade-in"
          />
          <Card
            icon="check"
            headline="Precision Insights"
            subhead="Harness predictive modeling and genomic data analysis to drive accurate, actionable insights tailored to research and healthcare objectives."
            className="action-on-view action-fade-in action-delay-250"
          />
          <Card
            icon="check"
            headline="Collaborative Expertise"
            subhead="Partner with a team experienced in supporting government initiatives to advance bioinformatics research and public health strategies."
            className="action-on-view action-fade-in action-delay-500"
          />
        </Box>
      </Section>

      <GlobalCallout />

      <Pathfinder />
    </Box>
  );
};

export default PageContent;
